import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import "antd/dist/reset.css";
import { Provider, useDispatch, useSelector } from "react-redux";
import { configureStore, createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { List, Card, Spin, Typography, Modal } from "antd";

const { Title } = Typography;

const fetchPokemons = createAsyncThunk("pokemon/fetchPokemons", async () => {
  const response = await axios.get("https://pokeapi.co/api/v2/pokemon?limit=50");

  const pokemonDetails = await Promise.all(
    response.data.results.map(async (pokemon) => {
      const details = await axios.get(pokemon.url);

      return {
        id: details.data.id,
        name: details.data.name,
        image: details.data.sprites.front_default,
        height: details.data.height,
        weight: details.data.weight,
        types: details.data.types.map((type) => type.type.name),
      };
    })
  );

  return pokemonDetails;
});

const pokemonSlice = createSlice({
  name: "pokemon",

  initialState: {
    pokemons: [],
    favorites: [],
    loading: false,
    error: null,
  },

  reducers: {
    toggleFavorite: (state, action) => {
      const exists = state.favorites.find(
        (pokemon) => pokemon.id === action.payload.id
      );

      if (exists) {
        state.favorites = state.favorites.filter(
          (pokemon) => pokemon.id !== action.payload.id
        );
      } else {
        state.favorites.push(action.payload);
      }
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchPokemons.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPokemons.fulfilled, (state, action) => {
        state.loading = false;
        state.pokemons = action.payload;
      })
      .addCase(fetchPokemons.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

const { toggleFavorite } = pokemonSlice.actions;

const store = configureStore({
  reducer: {
    pokemon: pokemonSlice.reducer,
  },
});

const typeColors = {
  fire: "#ff6b6b",
  water: "#4dabf7",
  grass: "#51cf66",
  electric: "#ffd43b",
  poison: "#b197fc",
  bug: "#94d82d",
  normal: "#ced4da",
  flying: "#74c0fc",
  ground: "#c92a2a",
  fairy: "#faa2c1",
};

const PokemonApp = () => {
  const dispatch = useDispatch();

  const { pokemons, loading, favorites } = useSelector(
  (state) => state.pokemon
);

  const [search, setSearch] = useState("");

  const [selectedPokemon, setSelectedPokemon] = useState(null);

  const [darkMode, setDarkMode] = useState(false);

  const [showFavorites, setShowFavorites] = useState(false);

  useEffect(() => {
    dispatch(fetchPokemons());
  }, [dispatch]);

  const filteredPokemons = pokemons.filter((pokemon) =>
  pokemon.name.toLowerCase().includes(search.toLowerCase())
);

const displayedPokemons = showFavorites
  ? filteredPokemons.filter((pokemon) =>
      favorites.find((fav) => fav.id === pokemon.id)
    )
  : filteredPokemons;


  return (
    <div
      style={{
        padding: "40px",
        minHeight: "100vh",
        background: darkMode ? "#141414" : "#f0f2f5",
      }}
    >
      <Title
        level={2}
        style={{
        textAlign: "center",
        marginBottom: 40,
        color: darkMode ? "#fff" : "#000",
      }}
     >
        Pokémon App
      </Title>


      <div style={{ textAlign: "center", marginBottom: 20 }}>
          <button
            onClick={() => setDarkMode(!darkMode)}
            style={{
              padding: "10px 20px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
           {darkMode ? "☀️ Light Mode" : "🌙 Dark Mode"}
          </button>

          <div style={{ textAlign: "center", marginBottom: 20 }}>
              <button
                onClick={() => setShowFavorites(!showFavorites)}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: "bold",
                  background: darkMode ? "#333" : "#eee",
                  color: darkMode ? "#fff" : "#000",
                }}
              >
                {showFavorites ? "🌎 Ver Todos" : "⭐ Ver Favoritos"}
              </button>
          </div>
      </div>

      <div style={{ textAlign: "center", marginBottom: 30 }}>
    <input
      type="text"
      placeholder="Pesquisar Pokémon..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      style={{
        padding: "12px",
        width: "300px",
        borderRadius: "8px",
        border: "1px solid #ccc",
        fontSize: "16px",
      }}
    />
</div>

      {loading ? (
        <div style={{ textAlign: "center", marginTop: 100 }}>
          <Spin size="large" />
        </div>
      ) : (
        <List
          grid={{
            gutter: 16,
            xs: 1,
            sm: 2,
            md: 3,
            lg: 4,
            xl: 5,
          }}
          

          dataSource={displayedPokemons}
          renderItem={(pokemon) => (
            <List.Item>
             <Card
                hoverable
                onClick={() => setSelectedPokemon(pokemon)}
                style={{
                  textAlign: "center",
                  borderRadius: 12,
                  transition: "0.3s",
                  cursor: "pointer",
                  background: darkMode ? "#1f1f1f" : "#fff",
                  color: darkMode ? "#fff" : "#000",
                }}
                cover={
                  <img
                    alt={pokemon.name}
                    src={pokemon.image}
                    style={{
                      width: 150,
                      height: 150,
                      objectFit: "contain",
                      margin: "20px auto 0",
                    }}
                  />
                }
              >
                <Card.Meta
                    title={
                      <span style={{ color: darkMode ? "#fff" : "#000" }}>
                        {pokemon.name.toUpperCase()}
                      </span>
                    }
                    description={
                      <span style={{ color: darkMode ? "#ddd" : "#555" }}>
                        #{pokemon.id}
                      </span>
                    }
                />
                <div style={{ marginTop: 10 }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(toggleFavorite(pokemon));
                      }}
                      style={{
                        padding: "8px 12px",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        background: darkMode ? "#333" : "#eee",
                        color: darkMode ? "#fff" : "#000",
                        fontWeight: "bold",
                      }}
                    >
                      {favorites.find((fav) => fav.id === pokemon.id)
                        ? "⭐ Favorito"
                        : "☆ Favoritar"}
                    </button>
                  </div>
              </Card>
            </List.Item>
          )}
        />
           )}
      
      <Modal
        open={!!selectedPokemon}
        footer={null}
        onCancel={() => setSelectedPokemon(null)}
      >
        {selectedPokemon && (
          <div style={{ textAlign: "center" }}>
            <img
              src={selectedPokemon.image}
              alt={selectedPokemon.name}
              style={{
                width: 200,
                height: 200,
                objectFit: "contain",
              }}
            />

            <h2>{selectedPokemon.name.toUpperCase()}</h2>

            <p>ID: #{selectedPokemon.id}</p>

            <p>Altura: {selectedPokemon.height}</p>

            <p>Peso: {selectedPokemon.weight}</p>

            <div>
              {selectedPokemon.types.map((type) => (
                <span
                  key={type}
                  style={{
                    background: typeColors[type] || "#999",
                    color: "#fff",
                    padding: "6px 12px",
                    borderRadius: "20px",
                    margin: "4px",
                    display: "inline-block",
                    fontWeight: "bold",
                  }}
                >
                  {type}
                </span>
              ))}
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <Provider store={store}>
    <PokemonApp />
  </Provider>
);